import React, { useState, useEffect } from 'react';
import { Bell, X, Check, Trash2, CheckCheck } from 'lucide-react';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import './NotificationCenter.css';

const NotificationCenter = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread
  const navigate = useNavigate();

  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {

    if (isOpen) {
      fetchNotifications();
      // Auto-refresh while the panel is open instead of requiring a close/reopen (item #24).
      const interval = setInterval(fetchNotifications, 20000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/notifications/');
      setNotifications(response.data);
      const unread = response.data.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Error fetching notifications:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.post(`/api/notifications/${id}/mark-read`);
      setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/api/notifications/${id}`);
      const deletedNotif = notifications.find(n => n.id === id);
      setNotifications(notifications.filter(n => n.id !== id));
      if (deletedNotif && !deletedNotif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    onClose();

    // Navigate based on entity type
    if (notification.related_entity_type === 'deal') {
      navigate(`/deal-details/${notification.related_entity_id}`);
    } else if (notification.related_entity_type === 'company') {
      navigate(`/companies`);
    } else if (notification.related_entity_type === 'customer') {
      navigate(`/customers`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.is_read)
    : notifications;

  return (
    <>
      {/* Notification Modal/Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="notification-center-backdrop"
            onClick={onClose}
          />

          {/* Panel */}
          <div className="notification-center-panel">
            {/* Header */}
            <div className="notification-center-header">
              <div className="notification-center-header-title">
                <Bell size={24} />
                <div>
                  <h2>Notifications</h2>
                  {unreadCount > 0 && (
                    <span className="notification-center-unread-text">
                      {unreadCount} unread
                    </span>
                  )}
                </div>
              </div>
              <button 
                onClick={onClose}
                className="notification-center-close"
              >
                <X size={24} />
              </button>
            </div>

            {/* Tabs & Actions */}
            <div className="notification-center-controls">
              <div className="notification-center-tabs">
                <button
                  onClick={() => setFilter('all')}
                  className={`notification-center-tab ${filter === 'all' ? 'active' : ''}`}
                >
                  All ({notifications.length})
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={`notification-center-tab ${filter === 'unread' ? 'active' : ''}`}
                >
                  Unread ({unreadCount})
                </button>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="notification-center-mark-all"
                >
                  <CheckCheck size={16} />
                  Mark all read
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="notification-center-list">
              {loading ? (
                <div className="notification-center-empty">
                  <Bell size={48} />
                  <p>Loading notifications...</p>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="notification-center-empty">
                  <Bell size={48} />
                  <p>
                    {filter === 'unread' ? "No unread notifications" : "No notifications yet"}
                  </p>
                  <span>
                    {filter === 'unread' ? "You're all caught up!" : "We'll notify you when something happens"}
                  </span>
                </div>
              ) : (
                filteredNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`notification-center-item ${!notif.is_read ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    {!notif.is_read && <div className="notification-center-item-indicator" />}
                    
                    <div className="notification-center-item-content">
                      <div className="notification-center-item-header">
                        <h4>{notif.related_entity_name || 'Notification'}</h4>
                        <span className="notification-center-item-time">
                          {formatDate(notif.created_at)}
                        </span>
                      </div>
                      <p className="notification-center-item-message">{notif.message}</p>
                      {notif.triggered_by_user_name && (
                        <span className="notification-center-item-user">
                          by {notif.triggered_by_user_name}
                        </span>
                      )}
                    </div>

                    <div className="notification-center-item-actions">
                      {!notif.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notif.id);
                          }}
                          title="Mark as read"
                        >
                          <Check size={18} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notif.id);
                        }}
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NotificationCenter;
